import { useEffect, useMemo, useState } from 'react';
import { AiFillFilePdf } from 'react-icons/ai';
import { FaFileDownload } from 'react-icons/fa';

import { useModal } from 'contexts';
import { getDidacticResourcesByCourse } from 'services';
import { DidacticResource } from 'types';

import './style.scss';

type ModalCourseResourcesProps = {
  courseID?: string;
  courseTitle?: string;
};

export function ModalCourseResources({ courseID, courseTitle }: ModalCourseResourcesProps) {
  const { closeModal } = useModal();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [resources, setResources] = useState<DidacticResource[]>([]);

  useEffect(() => {
    const loadResources = async () => {
      if (!courseID) {
        setResources([]);
        return;
      }

      setLoading(true);
      setError(false);

      try {
        const data = await getDidacticResourcesByCourse(courseID);
        setResources(data);
      } catch (e) {
        setError(true);
        setResources([]);
      } finally {
        setLoading(false);
      }
    };

    loadResources();
  }, [courseID]);

  const pdfResources = useMemo(() => {
    return resources.filter(resource => {
      const extension = resource.url?.extension?.toLowerCase();
      const contentType = resource.url?.content_type?.toLowerCase();
      return extension === '.pdf' || contentType === 'application/pdf';
    });
  }, [resources]);

  return (
    <section className="resources-modal">
      <header className="resources-modal__header">
        <div>
          <h2>Recursos didáticos</h2>
          {!!courseTitle && <p>{courseTitle}</p>}
        </div>
        <button type="button" onClick={closeModal} className="resources-modal__close">
          Fechar
        </button>
      </header>

      {loading && <div className="resources-modal__state">Carregando recursos...</div>}

      {!loading && error && (
        <div className="resources-modal__state resources-modal__state--error">
          Não foi possível carregar os recursos didáticos. Tente novamente.
        </div>
      )}

      {!loading && !error && !pdfResources.length && (
        <div className="resources-modal__state">Nenhum recurso didático disponível para este curso.</div>
      )}

      {!loading && !error && !!pdfResources.length && (
        <ul className="resources-modal__list">
          {pdfResources.map(resource => {
            const fileUrl = resource.url?.url || '';
            const fileName = resource.url?.filename || resource.title || 'Recurso didático';

            return (
              <li className="resources-modal__item" key={resource._id}>
                <div className="resources-modal__meta">
                  <span className="resources-modal__icon">
                    <AiFillFilePdf />
                  </span>
                  <div>
                    <h3>{resource.title || fileName}</h3>
                    {!!resource.description && <p>{resource.description}</p>}
                  </div>
                </div>

                <a
                  href={fileUrl}
                  download={fileName}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="resources-modal__download"
                >
                  <FaFileDownload />
                  <span>{fileName}</span>
                </a>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
